import { addRumError } from 'helpers/rum/addRumError';
import { addRumErrorThrottled } from 'helpers/rum/addRumErrorThrottled';
import { DATADOG_ENVIRONMENT } from 'state_management/env_constants';

import { getBoundConsole } from './getBoundConsole';
import { getBoundDatadogLogs } from './getBoundDatadogLogs';
import { LoggerData, LoggerGenericFn, LoggerMethod } from './loggerTypes';

/**
 * Production:
 * - Log warn and error to Datadog
 * - Log only unhandled exceptions to console
 *
 * Demo & Develop:
 * - Log everything to Datadog by default, but ultimately disable via feature-
 *    flag; see `disableDatadogLogging`
 * - Log everything to console
 *
 * Other (Local Development, One-Off Branches):
 * - Log everything to console
 */

const reportRumError = (data?: LoggerData) => {
  if (!data?.rum) return;

  try {
    const { rum, error } = data;

    if (rum.throttleKey) {
      addRumErrorThrottled({
        key: rum.throttleKey,
        error,
        context: rum.context,
        throttleMs: rum.throttleMs
      });
      return;
    }

    addRumError(error, rum.context);
  } catch {
    // Telemetry must never break application error handling
  }
};

const stripRumData = (data?: LoggerData) => {
  if (!data?.rum) return data;

  const { rum: _rum, ...logData } = data;
  return logData;
};

export const getLogger = (method: LoggerMethod): LoggerGenericFn => {
  const boundConsole = getBoundConsole(method);
  const boundDatadogLogs = getBoundDatadogLogs(method);

  switch (DATADOG_ENVIRONMENT) {
    case 'prod':
      return (message, data) => {
        if (method === 'error') {
          reportRumError(data);
        }

        const logData = method === 'error' ? stripRumData(data) : data;

        if (method === 'error' || method === 'warn' || data?.force) {
          boundDatadogLogs(message, logData);
        }
      };
    case 'demo':
    case 'develop':
      return (message, data) => {
        if (method === 'error') {
          reportRumError(data);
        }

        const logData = method === 'error' ? stripRumData(data) : data;

        boundDatadogLogs(message, logData);
        boundConsole(message, logData);
      };
    case '':
    default:
      return (message, data) => {
        if (method === 'error') {
          reportRumError(data);
        }

        const logData = method === 'error' ? stripRumData(data) : data;
        boundConsole(message, logData);
      };
  }
};
